/**
 * Renders a plain-text message body safely.
 *
 * SECURITY (spec §8 "Escaping / XSS"):
 *  - The body is PLAIN TEXT. We render it through React text nodes only —
 *    NEVER `dangerouslySetInnerHTML` — so any HTML a user types (`<script>`,
 *    `<img onerror=…>`) is escaped and inert.
 *  - Mention chips are built ONLY from the server-validated `mentions` UUID
 *    list. A token `@[Name](user:<uuid>)` is promoted to a chip ONLY when its
 *    UUID is present in `mentions`. A literal `@[Board President](user:abc)`
 *    a user types with an unvalidated/forged UUID is rendered as escaped
 *    inert text — never an authoritative chip. (Anti-impersonation: the
 *    server resolves/validates the set; the client never trusts raw body.)
 *
 * The display name inside the chip comes from the token's `Name` segment for
 * presentation only; it carries no authority and is rendered as a text node
 * (escaped). Authority lives entirely in the validated UUID match.
 */

// @[Display Name](user:<uuid>)
const MENTION_RE =
  /@\[([^\]]{1,80})\]\(user:([0-9a-fA-F-]{8,40})\)/g;

export default function MentionText({ body, mentions }) {
  if (!body) return null;
  const valid = new Set((mentions || []).map((m) => String(m).toLowerCase()));

  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  MENTION_RE.lastIndex = 0;
  while ((match = MENTION_RE.exec(body)) !== null) {
    const [raw, name, uuid] = match;
    const start = match.index;
    if (start > lastIndex) {
      nodes.push(
        <span key={`t${key++}`}>{body.slice(lastIndex, start)}</span>
      );
    }
    if (valid.has(String(uuid).toLowerCase())) {
      // Validated mention → inert chip (name is a text node = escaped).
      nodes.push(
        <span key={`m${key++}`} className="vdsc-mention">
          @{name}
        </span>
      );
    } else {
      // Unvalidated / forged token → render the literal text inert.
      nodes.push(<span key={`r${key++}`}>{raw}</span>);
    }
    lastIndex = start + raw.length;
  }
  if (lastIndex < body.length) {
    nodes.push(<span key={`t${key++}`}>{body.slice(lastIndex)}</span>);
  }
  // Preserve newlines as the surrounding CSS uses pre-wrap; nodes are text.
  return <span className="vdsc-body-text">{nodes}</span>;
}
